import React, { useEffect } from "react";
import _ from "lodash";
import cx from 'classnames';

import { animated, useSpring, useTransition } from "@react-spring/web";
import styles from "./styles.module.scss";
import { SearchBar } from "src/components/input";
import { TabBar } from "src/components/tabbar";
import { AssetPlaceholder, CollectionPlaceholder, CollectionsBrowser, EntityBrowser, StreamPlaceholder, StreamsBrowser } from "./browser";
import { useDispatch } from "react-redux";
import { invalidateAssetsSearchResults, invalidateCollectionsSearchResults, invalidateStreamsSearchResults, searchAssets, searchCollections, searchStreams } from "src/redux/actions/search";
import { useSelector } from "react-redux";
import { USER_DEFINED } from "src/constants/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import { useLocation } from "react-router-dom";
import { CreateAssetFileType } from "src/types/hub";

interface PageEntityProps {
  style: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  fileType: CreateAssetFileType;
}

interface PageEntityContextProps {
  fileType: CreateAssetFileType;
  query: string | null;
}

export const PageEntityContext = React.createContext<PageEntityContextProps>({
  fileType: CreateAssetFileType.PDF,
  query: ""
});

const PageEntity: React.FC<PageEntityProps> = ({ fileType, style, width, onSelection }) => {
  const [query, setQuery] = React.useState<string | null>('');

  const browserTransition = useTransition(!query, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const searchTransition = useTransition(query, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleQueryChange = (value) => {
    setQuery(value);
  }

  return (
    <animated.div className={cx(styles.pageView)} style={style}>
      <div className={styles.searchBar}>
        <SearchBar query={query} onSearch={handleQueryChange} />
      </div>
      <div className={cx(styles.stacks, styles.content)}>
        <PageEntityContext.Provider value={{ fileType, query }}>
          {browserTransition((style, item) => item && <AssetBrowser style={style} width={width} onSelection={onSelection} />)}
          {searchTransition((style, item) => item && <SearchResults style={style} width={width} query={query} onSelection={onSelection} />)}
        </PageEntityContext.Provider >
      </div>
    </animated.div >
  );
}

interface AssetBrowserProps {
  style: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const AssetBrowser: React.FC<AssetBrowserProps> = ({ style, width, onSelection }) => {
  const [activeTab, setActiveTab] = React.useState(0);

  const handleSelection = (entity) => {
    onSelection(entity);
  }

  return (
    <animated.div style={style} className={cx(styles.assetBrowser, styles.stack)}>
      <TabBar className={cx(styles.tabs)} gap={32} tabSize="equal" active={activeTab} onSelectionChange={(tab) => setActiveTab(tab)}>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>Streams</div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>Collections</div>
      </TabBar>
      <div className={cx(styles.stacks, styles.tabContainer)} style={{ width: width }}>
        <TabSection index={0} selected={activeTab}>
          <StreamsBrowser
            width={width - 60}
            action={entity => {
              if (entity.entityType === 'asset') {
                return (
                  <button className={styles.action}>
                    <i className={cx(styles.icon, 'paperflite-dropdown')}></i>
                  </button>
                );
              }

              return false;
            }}
            onSelection={handleSelection}
          />
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <CollectionsBrowser width={width - 60}
            action={entity => {
              if (entity.entityType === 'asset') {
                return (
                  <button className={styles.action}>
                    <i className={cx(styles.icon, 'paperflite-dropdown')}></i>
                  </button>
                );
              }

              return false;
            }}
            onSelection={handleSelection}
          />
        </TabSection>
      </div>
    </animated.div>
  );
}

interface SearchResultsProps {
  style: any;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ style, width, query, onSelection }) => {

  const { streams, collections, assets } = useSelector((state: any) => {
    const streamState = _.get(state, 'search.streams');
    const collectionState = _.get(state, 'search.collections');
    const assetState = _.get(state, 'search.assets');

    const streams = _.get(streamState, 'pagination.totalRecordsCount', 0);
    const collections = _.get(collectionState, 'pagination.totalRecordsCount', 0);
    const assets = _.get(assetState, 'pagination.totalRecordsCount', 0);

    return {
      streams: streams,
      collections: collections,
      assets: assets
    };
  }, _.isEqual);

  const [activeTab, setActiveTab] = React.useState(0);

  const handleSelection = (entity) => {
    onSelection(entity);
  }

  return (
    <animated.div style={style} className={cx(styles.searchResults, styles.stack)}>

      <div className={cx(styles.stacks, styles.tabContainer)}>
        <AssetSearchResult query={query} width={width - 60}
          action={entity => {
            if (entity.entityType === 'asset') {
              return (
                <button className={styles.action}>
                  <i className={cx(styles.icon, 'paperflite-dropdown')}></i>
                </button>
              );
            }

            return false;
          }}
          onSelection={handleSelection}
        />
      </div>
    </animated.div>
  );
}

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({ index, selected, children }) => {
  const active = index === selected;
  const [animation] = useSpring({
    height: active ? 'auto' : 0,
    opacity: active ? 1 : 0,
    transform: active ? `translate3d(0,0,0)` : `translate3d(${100 * (index > selected ? 1 : -1)}%, 0, 0)`,
  }, [active]);

  return (
    <animated.div style={animation} className={cx(styles.tabBody, styles.stack)}>
      {children}
    </animated.div>
  );
}


interface AssetSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const AssetSearchResult: React.FC<AssetSearchResultProps> = ({ className, query, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, assets, nextPage, hasMoreItems } = useSelector((state: any) => {
    const assetState = _.get(state, 'search.assets');
    const loading = _.get(assetState, 'loading');
    const pagination = _.get(assetState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const assets = (_.get(assetState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      assets: assets,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchAssetForPage(0);
  }, []);

  const searchAssetForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };

      if(page <= 0) {
        invalidateAssetsSearchResults();
      }
      dispatch(searchAssets(request));
    }
  }

  const handleLoadMoreAssets = () => {
    if (nextPage) {
      searchAssetForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreAssets}
        loader={() => {
          if (_.size(assets) === 0) {
            return (<>
              <AssetPlaceholder />
              <AssetPlaceholder />
              <AssetPlaceholder />
              <AssetPlaceholder />
            </>)
          } else {
            return <AssetPlaceholder />
          }
        }}
      >
        <EntityBrowser className={className}
          width={width}
          items={assets}
          action={action}
          onSelection={onSelection}
          loading={loading}
        />
      </InfiniteScroll>
    </div>
  );
}


export { PageEntity };